"use client";

import classNames from "classnames";
import { getTrackingDataset } from "@/tracking/utils";
import { SelectContentEventSchema } from "@/tracking/e-commerce/events-schemas";
import {
  Button,
  Card,
  Dialog,
  Typography,
} from "@cappelendamm/design-system/react";
import { useRef, FC, ReactNode } from "react";
import { useUniverseTheme } from "@/theming/context";
import { getThemeClassName } from "@/theming/selectors";
import { Xmark } from "iconoir-react";
import Link from "next/link";
import { useAuth } from "@clerk/nextjs";
import { usePathname } from "next/navigation";

type UnsafeLoginWrapperProps = {
  children: ReactNode;
  contentId?: string;
  contentType?: string;
  href: string;
  rel?: string;
  target?: string;
};

export const UnsafeLoginWrapper: FC<UnsafeLoginWrapperProps> = ({
  children,
  contentId,
  contentType,
  href,
  rel,
  target,
}) => {
  const ref = useRef<HTMLDialogElement>(null);
  const theme = useUniverseTheme();

  const { isSignedIn } = useAuth();

  const pathName = usePathname();
  const targetUrl = `${pathName}#${contentId}`;

  if (isSignedIn) {
    return (
      <Card
        as={Link}
        href={href}
        rel={rel}
        target={target}
        className={classNames(
          "group @container/card relative grid h-full active:outline-3 active:outline-offset-4 active:outline-[var(--cvr-canvas-background)]",
        )}
        {...getTrackingDataset(
          SelectContentEventSchema.parse({
            eventName: "select_content",
            content_id: contentId,
            content_type: contentType,
          }),
        )}
      >
        {children}
      </Card>
    );
  }

  return (
    <>
      <Card
        role="button"
        onClick={() => {
          if (ref.current) {
            ref.current.showModal();
          }
        }}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (ref.current) {
              ref.current.showModal();
            }
          }
        }}
        className={classNames(
          "group @container/card relative grid h-full cursor-pointer focus:outline-3 focus:outline-offset-4 focus:outline-[var(--cvr-canvas-background)] active:outline-3 active:outline-offset-4 active:outline-[var(--cvr-canvas-background)]",
        )}
        {...getTrackingDataset(
          SelectContentEventSchema.parse({
            eventName: "select_content",
            content_id: contentId,
            content_type: contentType,
          }),
        )}
      >
        {children}
      </Card>
      <Dialog
        ref={ref}
        className="max-w-2xs grid-cols-[auto] content-start gap-4 open:grid sm:inset-0 sm:m-auto sm:min-w-2xl sm:content-normal"
        style={{ ...theme }}
      >
        <div className="p-0 sm:p-2">
          <div
            className={classNames(
              "alig flex w-full items-center justify-between",
              getThemeClassName(null),
            )}
          >
            <Typography variant="title-lg" as="h2">
              Du må logge inn
            </Typography>
            <button
              onClick={() => ref.current?.close()}
              className={classNames(
                "rounded-full p-2",
                getThemeClassName(null, { interactive: true }),
              )}
            >
              <Xmark width={24} height={24} />
              <span className="sr-only">Lukk</span>
            </button>
          </div>

          <Typography variant="body-lg" className="mt-4">
            Denne handlingen krever at du er logget inn. Vennligst logg inn for
            å fortsette.
          </Typography>
          <Button
            as={Link}
            href={`/logg-inn?redirect_url=${encodeURIComponent(targetUrl)}`}
            rel={rel}
            target={target}
            className="mt-4"
          >
            Logg inn og fortsett
          </Button>
          <Button
            onClick={() => ref.current?.close()}
            variant="secondary"
            className="mt-4 w-full"
          >
            Lukk
          </Button>
        </div>
      </Dialog>
    </>
  );
};
